import { isEmpty } from 'Helpers/objects';
import { mapStateToCategories } from 'Actions/categories';
import { getSelectedLangCode } from 'Selectors/config';
import { createSelector } from 'reselect';
import { itemsMetadataSelector } from './items';
import { paramsToQueryString } from 'Helpers/url';
import { urls } from 'Reducers/categories';
import { getType } from 'Reducers/helpers/typeModifier';

import { EMPTY_CATEGORY } from 'Constants/categories';
import { getAppliedFilterCategoryId } from './filters';

const isFetching = (state, type) => state.categories.isFetching[type];

const flatFilters = filters =>
    filters.reduce((acc, curr) => {
        let child = [];

        if (curr.children && curr.children.length > 0) {
            child = flatFilters(curr.children);
        }
        return [...acc, curr, ...child];
    }, []);

const recursiveIterator = (search = '', options = [], childKey = 'child') =>
    options.reduce((acc, curr) => {
        let child = {};

        if (curr[childKey] && curr[childKey].length > 0) {
            child = recursiveIterator(search, curr[childKey], childKey);
        }
        if (search === curr.id) {
            return curr;
        }
        return { ...acc, ...child };
    }, {});

const getCategoryById = createSelector(
    mapStateToCategories,
    (_, id) => id,
    (categories, id) => {
        const findCategory = recursiveIterator(id, categories, 'sub_categories');

        return isEmpty(findCategory) ? undefined : findCategory;
    }
);

const categorySelector = (state, id) => state.categories.elements[id] || undefined;

const categoryIdSelector = (state, params) => params.categoryID || getAppliedFilterCategoryId(state, params);

const getCategoriesById = state => state.categories.elements;

const getCategoryElement = (state, params) =>
    state.categories.elements[params.categoryId] || EMPTY_CATEGORY;

const categoriesByFacet = (categories, flatted) =>
    categories.reduce((prev, curr) => {
        if (Object.prototype.hasOwnProperty.call(flatted, curr.id)) {
            const count = flatted[curr.id];
            let child = [];

            if (curr.sub_categories) {
                child = categoriesByFacet(curr.sub_categories, flatted);
            }

            if (child.length > 0) {
                return [...prev, { ...curr, count, sub_categories: child }];
            }
            return [...prev, { ...curr, count }];
        }
        return prev;
    }, []);

const categoriesByFilterSelector = createSelector(
    mapStateToCategories,
    itemsMetadataSelector,
    (categories, itemsMetadata) => {
        const { filters = [] } = itemsMetadata || {};
        const categoryFilter = filters.find(filter => filter.id === 'category');

        if (categoryFilter && categoryFilter.values) {
            const flatted = flatFilters(categoryFilter.values).reduce(
                (prev, curr) => ({
                    ...prev,
                    [curr.id]: curr.count
                })
                , {});

            return categoriesByFacet(categories, flatted);
        }
        return categories;
    }
);

const getCategoryElements = state => state.categories.elements;

const categoryHasPrice = createSelector(
    getCategoriesById,
    (_, props) => {
        return props.item.category_id;
    },
    (categories, categoryId) => {
        const category = categories[categoryId];

        return category && category.params && !!category.params.find(rule => rule.key === 'price');
    }
);

const getPostingAttributes = createSelector(
    getCategoriesById,
    (_, props) => {
        return props.item.category_id;
    },
    (categories, categoryId) => {
        const category = categories[categoryId];

        return category && category.params && category.params.filter(
            param => (param.definitions.posting.key)
        );
    }
);

const mapParams = (param, result) => {
    param.forEach(elem => {
        const values = {};

        if (elem.values && elem.values.length > 0) {
            elem.values.forEach(value => {
                values[value.key] = value.name;
                if (value.params && value.params.length > 0) {
                    mapParams(value.params, result);
                }
            });
        }
        result[elem.key] = { ...result[elem.key], ...values };
    });
};

const getPostingAttributesMap = createSelector(
    getPostingAttributes,
    attributes => {
        const result = {};

        mapParams(attributes, result);
        return result;
    }
);

const isFetchingCategoryById = (state, categoryID) => {
    const url = `${urls.getCategory(categoryID)}?${paramsToQueryString({ full: true })}`;
    const type = getType('ELEMENT_CATEGORIES', url);

    return isFetching(state, type);
};

const mapSubcategories = (categoryElements, category) => {
    const newCategory = { ...category };

    if (category.sub_categories) {
        newCategory.sub_categories = category.sub_categories.map(id => {
            const subcategory = categoryElements[id];

            if (subcategory && subcategory.sub_categories) {
                return mapSubcategories(categoryElements, subcategory);
            }

            return subcategory;
        });
    }
    return newCategory;
};

const selectCategoryElements = ({ categories }) => categories.elements;
const selectCategoryCollections = ({ categories }) => categories.collections;
const selectCategoryMetadata = ({ categories }) => categories.collectionMetadata;

const selectMappedCategories = createSelector(
    selectCategoryElements,
    selectCategoryCollections,
    selectCategoryMetadata,
    getSelectedLangCode,
    (elements, collections, metadatas, langCode) => {
        let url = urls.getCategories;

        if (langCode) {
            url = `${url}?lang=${langCode}`;
        }

        const type = getType('CATEGORIES', url);

        const collection = collections[type] || [];
        const metadata = metadatas[type];

        const result = collection.map(id => ({ ...elements[id] }));

        if (metadata) {
            result.total = metadata.total;
            result.cursor = metadata.cursor;
            result.nextPageUrl = metadata.nextPageUrl;
        }

        return result.map(category => mapSubcategories(elements, category));
    }
);

export {
    getCategoryById,
    categorySelector,
    getCategoriesById,
    getCategoryElement,
    getCategoryElements,
    categoriesByFilterSelector,
    categoryHasPrice,
    getPostingAttributes,
    getPostingAttributesMap,
    isFetchingCategoryById,
    selectMappedCategories,
    categoryIdSelector
};
